import React, { FC, useCallback, useEffect, useState } from 'react';
import { OptionProps, SelectProps, SelectValue } from 'antd/lib/select';

import { debounce } from './utilsHelper/debounce';

import { OptionType, SamoletSelectProps } from './types';

import DefaultNotFoundContent from './components/DefaultNotFoundContent';

import { useApi } from './hooks/useApi';

import { BaseSelect } from './BaseSelect';

import { Label, CaptionText } from 'ui/components/Input/styled';

export const NewSelect: FC<SamoletSelectProps> = ({
  label,
  caption,
  onCreateNewOption,
  createNewOptionText,
  onSearch,
  apiUrl,
  additionalField,
  onAdditionalFieldClick,
  options: propsOptions,
  search: propsSearch,
  value: propsValue,
  defaultValue,
  customOptions,
  name,
  renderOption,
  loadOnOpen,
  loadOption,
  loadOnEveryOpen,
  onLoaded,
  notFoundContentProps,
  ...props
}) => {
  const [searchValue, setSearchValue] = useState<string | undefined>('');
  const [currentValue, setCurrentValue] = useState<SelectValue | undefined>();
  const [options, setOptions] = useState<OptionType[]>(
    customOptions || (propsOptions as OptionType[]) || [],
  );
  const [dropdownVisible, setDropdownVisible] = useState(false);

  useEffect(() => {
    setSearchValue(propsSearch);
  }, [propsSearch]);

  useEffect(() => {
    setCurrentValue(propsValue);
  }, [propsValue]);

  const { isLoading, loadedOptions, onOpenHandle, onSearchHandle, onSelectHandle } = useApi({
    apiUrl,
    defaultValue,
    name,
    onSearch,
    renderOption,
    propsValue,
    loadOnOpen,
    loadOnEveryOpen,
    loadOption,
    onLoaded,
  });

  useEffect(() => {
    let selectOptions = options;

    if (customOptions) {
      selectOptions = customOptions;
    }

    if (apiUrl) {
      selectOptions = loadedOptions;
    }

    if (additionalField && !onAdditionalFieldClick) {
      selectOptions = [
        { label: additionalField.label, value: additionalField.name },
        ...selectOptions,
      ];
    }
    setOptions(selectOptions);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customOptions, apiUrl, loadedOptions, additionalField, onAdditionalFieldClick]);

  const handleSearch = useCallback(
    (search: string) => {
      setSearchValue(search);
      onSearch && onSearch(search);
      onSearchHandle(search);
    },
    [onSearch, setSearchValue, onSearchHandle],
  );

  const handleChange: SelectProps<SelectValue>['onChange'] = useCallback(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (value: any, options: any) => {
      setCurrentValue(value);
      props.onChange && props.onChange(value, options);
    },
    [props],
  );

  const handleSelect = useCallback(
    (value: SelectValue, option: OptionProps) => {
      if (value === additionalField?.name && onAdditionalFieldClick) {
        onAdditionalFieldClick();
      }

      onSelectHandle(searchValue);
      setSearchValue('');

      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const selectedValue = options.find(({ id }: any) => id === Number(option.value)) || {};
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      props.onSelect && props.onSelect(value as any, option, selectedValue);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      props.onSelect,
      apiUrl,
      onSelectHandle,
      searchValue,
      options,
      setSearchValue,
      additionalField,
      onAdditionalFieldClick,
    ],
  );

  const handleOpen = useCallback(
    (open: boolean) => {
      onOpenHandle(open);

      setDropdownVisible(open);
      props.onDropdownVisibleChange && props.onDropdownVisibleChange(open);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [apiUrl, loadOnOpen, onOpenHandle, options.length, loadOnEveryOpen],
  );

  const defaultOnSearch = props.showSearch ? debounce(handleSearch, 200) : undefined;

  return (
    <>
      {label && <Label>{label}</Label>}

      <BaseSelect
        name={name}
        defaultValue={defaultValue}
        value={currentValue}
        loading={isLoading}
        onChange={handleChange}
        onSearch={defaultOnSearch}
        onSelect={handleSelect}
        notFoundContent={
          <DefaultNotFoundContent
            loading={isLoading}
            searchValue={searchValue}
            onCreateNewOption={onCreateNewOption}
            createNewOptionText={createNewOptionText}
            {...notFoundContentProps}
          />
        }
        onDropdownVisibleChange={handleOpen}
        open={dropdownVisible}
        options={options}
        additionalField={additionalField}
        onAdditionalFieldClick={onAdditionalFieldClick}
        {...props}
      />

      {caption && <CaptionText $hasError={props.hasError}>{caption}</CaptionText>}
    </>
  );
};

NewSelect.defaultProps = {
  placeholder: 'Выберите',
  loadOnOpen: false,
  loadOption: false,
  loadOnEveryOpen: false,
};
